import {
  Icon,
  Text,
  useIsMobile,
  VisualPickerPanel,
  type Omittable,
  type VisualPickerPanelProps,
} from "@salestrak/ui";

import { NO_PERMISSION } from "#app/components/menus/disabled-reason";
import type { MenuParts } from "#app/components/menus/menu-parts";
import {
  useAppearanceSheetActions,
  type AppearanceSheetTarget,
} from "#app/lib/appearance-sheet-context";

type AppearanceMenuRowProps = Omittable<{
  parts: MenuParts;
  /** The row stays seated and says why when the viewer cannot edit. */
  disabled: boolean;
  /** What the phone sheet edits. */
  target: AppearanceSheetTarget;
  /** The desktop submenu's panel — the same live-save pair. */
  panel: VisualPickerPanelProps;
}>;

/**
 * The entity menus' "Color & icon" row. On desktop it is a submenu seating
 * `VisualPickerPanel` full-bleed (the panel owns its own header and insets,
 * the same seating the entity selectors use). On a phone a submenu has no
 * room to hang a picker off a row, and a drawer rendered inside the menu
 * would unmount with it — so the row is a plain item that closes the menu
 * and opens the app-level appearance sheet.
 */
export function AppearanceMenuRow({
  parts,
  disabled,
  target,
  panel,
}: AppearanceMenuRowProps) {
  const isMobile = useIsMobile();
  const { openAppearanceSheet } = useAppearanceSheetActions();
  const { Item, Sub, SubTrigger, SubPopup } = parts;

  const label = (
    <>
      <Icon color="muted" name="palette" size="md" />
      <Text>Color &amp; icon</Text>
    </>
  );

  if (isMobile) {
    return (
      <Item
        {...(disabled
          ? { disabled: true, disabledReason: NO_PERMISSION }
          : { onClick: () => openAppearanceSheet(target) })}
      >
        {label}
      </Item>
    );
  }

  return (
    <Sub>
      <SubTrigger disabled={disabled} disabledReason={NO_PERMISSION}>
        {label}
      </SubTrigger>
      <SubPopup padding="none">
        <VisualPickerPanel {...panel} />
      </SubPopup>
    </Sub>
  );
}
